import { Component, Input } from '@angular/core';
import { rawModes } from 'src/app/shared/constants/constants';

const rawModeAbbreviationOverrides: Record<string, string> = {
    Incremental_CDC: 'CDC',
    Streaming: 'STRM'
};

@Component({
    selector: 'cdp-job-chip',
    templateUrl: './job-chip.component.html',
    styleUrls: ['./job-chip.component.scss'],
    standalone: false
})
export class JobChipComponent {
    /** Search string to use for highlighting */
    @Input() search = '';

    /** Id of the job, if we're representing a single job. */
    @Input() set jobId(id: string) {
        this._jobId = id;
        this.refreshDesc();
    }
    public get jobId(): string {
        return this._jobId;
    }
    private _jobId: string;

    /** If we're representing multiple jobs, the count of jobs */
    @Input() set jobCount(count: number) {
        this._jobCount = count;
        this.refreshDesc();
    }
    public get jobCount(): number {
        return this._jobCount;
    }
    private _jobCount = 0;

    /** If we're representing multiple jobs, the count of jobs */
    @Input() set activeJobCount(count: number) {
        this._activeJobCount = count;
        this.refreshDesc();
    }
    public get activeJobCount(): number {
        return this._activeJobCount;
    }
    private _activeJobCount = 0;

    /** The size of the job chip, defaults to small */
    @Input() set size(size: 'sm' | 'xs') {
        this._size = size;
    }
    public get size(): string {
        return this._size;
    }
    private _size = 'sm';

    /** True if the job is active, or, true if ANY of the jobs represented by this chip are active */
    @Input() set isActive(_isActive: boolean) {
        this._isActive = _isActive;
        this.refreshDesc();
    }
    public get isActive(): boolean {
        return this._isActive;
    }
    private _isActive = false;

    /** String representation of job raw mode */
    @Input() set mode(mode: string) {
        const modeObj = Object.values(rawModes).find(m => m.name === mode);
        if (!modeObj && !rawModeAbbreviationOverrides[mode]) {
            throw new Error(`Invalid mode for job chip component: ${mode}`);
        }
        this._mode = modeObj ?? { name: mode, abbreviation: rawModeAbbreviationOverrides[mode] };
        this.refreshDesc();
    }
    public get mode(): string {
        return this._mode?.name;
    }
    public get modeCode(): string {
        return this._mode?.abbreviation || '???';
    }
    public get modeName(): string {
        return this._mode?.name || 'Unknown';
    }
    private _mode: { name: string; abbreviation: string };

    public get tooltip(): string {
        return this._tooltip;
    }
    private _tooltip = '';

    public get displayStr(): string {
        return this._displayStr;
    }
    private _displayStr = '';

    public refreshDesc(): void {
        let tooltip = '';

        if (this._jobCount < 1) {
            tooltip += `Mode: ${this.modeName}`;
            tooltip += this._jobId ? `, Id: ${this._jobId}` : '';
            tooltip += `, Active: ${this.isActive ? 'Yes' : 'No'}`;
        }

        if (this._jobCount > 0) {
            tooltip += `${this._jobCount} ${this.modeName.toLowerCase()} job${this.jobCount === 1 ? '' : 's'}`;
            tooltip +=
                this.jobCount > 1
                    ? ` (${this._activeJobCount} active, ${this._jobCount - this._activeJobCount} inactive)`
                    : '';
            tooltip += this.jobCount === 1 ? ` (${this._activeJobCount === 1 ? 'active' : 'inactive'})` : '';
        }
        this._tooltip = tooltip;
        this._displayStr = `${this.modeCode}${this.jobCount > 1 ? ' (' + this.jobCount + ')' : ''}`;
    }
}
